import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { DataCatalogModal } from "../dataCatalog/DataCatalog";
import { useDataCatalogSearch } from "../../hooks/useDataCatalogSearch";
import {
  setQuery,
  setPendingSegments,
  setLastSearchSegments,
  resetParameters,
} from "../../store/searchPage";
import { segmentsContainRealPill } from "../../lib/search/segmentsContainRealPill";
import { parametersToSegments } from "../../lib/search/parametersToSegments";
import { segmentsToText } from "../../lib/search/segmentsToText";
import { searchSuggestions } from "../../pages/search/config";
import TokenizedSearchInput from "./TokenizedSearchInput";

const CAROUSEL_INTERVAL = 3000;

/**
 * Thin adapter between Redux/routing and TokenizedSearchInput, which owns the actual
 * typing/tokens/palette state locally. Two external-write channels feed it, both one-shot:
 * `state.search.query` (a Data Catalog selection -- plain string, seeds via `initialQuery`) and
 * `state.search.pendingSegments` (a template click, or the backend's translated parse -- raw
 * segments, seeds via `initialSegments`, resolved into real tokens).
 *
 * `pendingSegments` is cleared via TokenizedSearchInput's own `onInitialSegmentsResolved`
 * callback, fired only once it has ACTUALLY resolved the segments -- clearing it the instant it's
 * handed down as a prop would lose it if the term index is still loading when it arrives.
 *
 * `lastSearchSegments` fallback: a remount (results-page resubmit, or Back/Forward to the landing
 * page) loses `pendingSegments` and local TokenizedSearchInput state alike -- `lastSearchSegments`
 * survives since searchPage.js's `setQuery` reducer keeps it cleared whenever `query` changes out
 * from under it, so it's never stale relative to the `query` this bar is about to show anyway.
 */
const SearchBar = ({
  useCarousel = true,
  staticPlaceholder = "Search",
  richPlaceholderSegments = null,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const query = useSelector((state) => state.search.query);
  const pendingSegments = useSelector((state) => state.search.pendingSegments);
  const lastSearchSegments = useSelector((state) => state.search.lastSearchSegments);
  const resultsQuery = useSelector((state) => state.search.resultsQuery);
  const knownParse = useSelector((state) => state.search.knownParse);
  const initialSegments =
    pendingSegments ?? (segmentsContainRealPill(lastSearchSegments) ? lastSearchSegments : null);
  const [currentSuggestionIndex, setCurrentSuggestionIndex] = useState(0);
  const [isCatalogOpen, setIsCatalogOpen] = useState(false);
  const { applySelection } = useDataCatalogSearch();

  useEffect(() => {
    if (!useCarousel) return undefined;
    const interval = setInterval(() => {
      setCurrentSuggestionIndex((i) => (i + 1) % searchSuggestions.length);
    }, CAROUSEL_INTERVAL);
    return () => clearInterval(interval);
  }, [useCarousel]);

  // useCallback: a fresh function identity every render would retrigger TokenizedSearchInput's
  // own resolution effect (this callback is one of its dependencies) with nothing really changed.
  const handleInitialSegmentsResolved = useCallback(() => {
    dispatch(setPendingSegments(null));
  }, [dispatch]);

  const placeholder = useCarousel
    ? searchSuggestions[currentSuggestionIndex] || staticPlaceholder
    : staticPlaceholder;

  // `lastSearchSegments` persists for the whole results-page visit (unlike one-shot
  // `pendingSegments`), so Save Search can record it later and `initialSegments` above can read
  // it as a remount-proof fallback. `resetParameters`: the PREVIOUS search's `parameters`
  // otherwise linger in the store until the new fetch's own `pending` action clears them a render
  // later -- during that window SearchResults.js's translate-from-parameters effect could mistake
  // them for belonging to this brand new, not-yet-resolved submission and re-inject the old
  // search's pills. Clearing synchronously here closes that window. Not cleared when the search on
  // screen is re-submitted: SearchResults.js skips a query equal to `resultsQuery`, so nothing would
  // refill `parameters` ("You're viewing…" and the #/% toggle would disappear).
  //
  // The pills a known parse translates to are text the server never saw: the parsed text is searched
  // instead, so the URL (all a refresh keeps) hits the cache.
  const handleSubmit = (text, segments) => {
    const isPillText =
      Boolean(knownParse) && text === segmentsToText(parametersToSegments(knownParse.parameters));
    const searchedText = isPillText ? knownParse.query : text;
    dispatch(setQuery(searchedText));
    dispatch(setLastSearchSegments(segments ?? null));
    if (searchedText !== resultsQuery) {
      dispatch(resetParameters());
    }
    navigate(`/search-results?query=${encodeURIComponent(searchedText)}`);
  };

  return (
    <>
      <TokenizedSearchInput
        initialQuery={query}
        initialSegments={initialSegments}
        onInitialSegmentsResolved={handleInitialSegmentsResolved}
        placeholder={placeholder}
        richPlaceholderSegments={richPlaceholderSegments}
        onSubmit={handleSubmit}
        onOpenCatalog={() => setIsCatalogOpen(true)}
        // Real bug report: the Data Catalog kept showing a previous selection after the bar was
        // cleared -- it seeds from `state.search.parameters`, a copy this component's own clear
        // button never touched. Resetting it here keeps the two in sync, same as every new
        // submit already does (see handleSubmit's own resetParameters, above).
        //
        // Also clears query/lastSearchSegments -- otherwise a later pendingSegments seed falls
        // back to this stale value once consumed, snapping the bar back to what was cleared.
        onClear={() => {
          dispatch(resetParameters());
          dispatch(setQuery(""));
          dispatch(setLastSearchSegments(null));
        }}
      />
      <DataCatalogModal
        isOpen={isCatalogOpen}
        onClose={() => setIsCatalogOpen(false)}
        onApplySelection={applySelection}
      />
    </>
  );
};

export default SearchBar;
